const DB_NAME = "elog_offline_v1";
const STORE = "requests";
const DB_VERSION = 1;
const MAX_QUEUE_ITEMS = 100;
const MAX_QUEUE_BYTES = 20 * 1024 * 1024;
const MAX_ITEM_BYTES = 2 * 1024 * 1024;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Offline replay is a business capability, not a generic transport retry.
// Only routes with server-side idempotency and no credentials/signatures/files
// are allowed here.
const OFFLINE_SAFE_ROUTES = [
  { method: "POST", path: /^\/v2\/voyage\/deck-log\/[^/]+$/ },
  { method: "POST", path: /^\/v2\/voyage\/engine-log\/[^/]+$/ },
  // The client supplies a stable submission_uid and the server protects this
  // route with persisted idempotency, so an onboard retry cannot duplicate a
  // checklist submission.
  { method: "POST", path: /^\/v2\/sms\/checklists\/[^/]+\/submissions$/ },
];

export function canQueueOffline({ method, url } = {}) {
  try {
    const pathname = new URL(String(url || ""), window.location.origin).pathname;
    const normalizedMethod = String(method || "").toUpperCase();
    return OFFLINE_SAFE_ROUTES.some(
      (entry) => entry.method === normalizedMethod && entry.path.test(pathname)
    );
  } catch {
    return false;
  }
}

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizedHeaders = (headers = {}) => {
  const source = typeof headers?.toJSON === "function" ? headers.toJSON() : headers;
  return Object.entries(source || {}).reduce((result, [name, value]) => {
    const key = String(name).toLowerCase();
    // Authentication is refreshed at replay time and must not be persisted in IndexedDB.
    if ([
      "authorization",
      "proxy-authorization",
      "cookie",
      "set-cookie",
      "host",
      "content-length",
      "x-api-key",
      "x-iship-api-key",
    ].includes(key)) return result;
    result[name] = value;
    return result;
  }, {});
};

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function runStore(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const result = fn(store);
        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function serializeRequestData(data) {
  if (!(data instanceof FormData)) {
    return { kind: "json", payload: data ?? null };
  }

  const entries = [];
  for (const [name, value] of data.entries()) {
    if (value instanceof Blob) {
      entries.push({
        name,
        type: "file",
        base64: await blobToBase64(value),
        filename: value.name || "upload.bin",
        mime: value.type || "application/octet-stream",
      });
    } else {
      entries.push({ name, type: "text", value: String(value) });
    }
  }
  return { kind: "formData", entries };
}

export function deserializeRequestData(serialized) {
  if (!serialized || serialized.kind === "json") {
    return serialized?.payload ?? serialized;
  }

  const form = new FormData();
  (serialized.entries || []).forEach((entry) => {
    if (entry.type === "file") {
      const binary = atob(entry.base64 || "");
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      form.append(entry.name, new Blob([bytes], { type: entry.mime }), entry.filename);
    } else {
      form.append(entry.name, entry.value);
    }
  });
  return form;
}

export async function enqueueRequest({ method, url, data, headers, requestId }) {
  if (!canQueueOffline({ method, url })) {
    throw new Error("This operation is not approved for offline replay");
  }
  const serializedData = await serializeRequestData(data);
  if (serializedData.kind !== "json") {
    throw new Error("File and form uploads cannot be queued offline");
  }
  const itemBytes = new TextEncoder().encode(JSON.stringify(serializedData)).byteLength;
  if (itemBytes > MAX_ITEM_BYTES) {
    throw new Error("Offline request is too large");
  }

  const existing = await getQueuedRequests();
  const cutoff = Date.now() - MAX_AGE_MS;
  const expired = existing.filter(
    (row) => !row.created_at || new Date(row.created_at).getTime() < cutoff
  );
  await Promise.all(expired.map((row) => clearRequest(row.id)));
  const retained = existing.filter((row) => !expired.includes(row));
  const retainedBytes = retained.reduce(
    (sum, row) => sum + new TextEncoder().encode(JSON.stringify(row.data || null)).byteLength,
    0
  );
  if (retained.length >= MAX_QUEUE_ITEMS || retainedBytes + itemBytes > MAX_QUEUE_BYTES) {
    throw new Error("Offline queue limit reached; reconnect before adding more records");
  }

  const stableRequestId = requestId || createRequestId();
  const item = {
    request_id: stableRequestId,
    method: (method || "POST").toUpperCase(),
    url,
    data: serializedData,
    headers: {
      ...normalizedHeaders(headers),
      "X-Request-Id": stableRequestId,
      "Idempotency-Key": stableRequestId,
    },
    created_at: new Date().toISOString(),
    retries: 0,
  };
  await runStore("readwrite", (store) => store.add(item));
  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
  return item;
}

export async function getQueuedRequests() {
  return runStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function getQueueCount() {
  const rows = await getQueuedRequests();
  return rows.length;
}

export async function clearRequest(id) {
  await runStore("readwrite", (store) => store.delete(id));
  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
}

export async function clearOfflineQueue() {
  const rows = await getQueuedRequests().catch(() => []);
  await Promise.all(rows.map((row) => clearRequest(row.id)));
  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
}

async function markRequestFailed(row, error) {
  const updated = {
    ...row,
    retries: Number(row.retries || 0) + 1,
    last_error: String(error?.message || "Synchronization failed").slice(0, 500),
    last_attempt_at: new Date().toISOString(),
  };
  await runStore("readwrite", (store) => store.put(updated));
}

export async function getQueueSummary() {
  const rows = await getQueuedRequests();
  return {
    count: rows.length,
    failedCount: rows.filter((row) => Number(row.retries || 0) > 0).length,
    retryCount: rows.reduce((sum, row) => sum + Number(row.retries || 0), 0),
    oldestQueuedAt: rows.map((row) => row.created_at).filter(Boolean).sort()[0] || null,
  };
}

export async function flushQueue(executor) {
  const rows = await getQueuedRequests();
  if (!rows.length) return { flushed: 0, failed: 0 };

  let flushed = 0;
  let failed = 0;

  for (const row of rows) {
    if (!canQueueOffline(row)) {
      await clearRequest(row.id);
      failed += 1;
      continue;
    }
    try {
      const body = deserializeRequestData(row.data);
      await executor({
        ...row,
        request_id: row.request_id || createRequestId(),
        body,
      });
      await clearRequest(row.id);
      flushed += 1;
    } catch (error) {
      await markRequestFailed(row, error);
      failed += 1;
    }
  }

  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
  return { flushed, failed };
}
